import { FC, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, rem, Text } from '@bonusly/design-system';

import { shouldIgnoreDoodleShortcut } from '../../doodle-editor/keyboard-shortcuts';
import { useSuppressBizyFlight } from '../use-bizy-flight';
import { DoodleCanvas } from './doodle-canvas';
import { DoodleToolbar } from './doodle-toolbar';
import { hasRenderedContent } from './stroke-utils';
import { useDoodle, type DoodleStroke } from './use-doodle';

export interface DoodleModalProps {
	isOpen: boolean;
	onClose: () => void;
	/**
	 * Called with the edited strokes when the user finishes. The modal is
	 * edit-only — rasterizing/uploading happens at the doodle_card on
	 * "Add to recognition".
	 */
	onSave: (strokes: DoodleStroke[]) => void;
	/**
	 * Seed strokes to edit (the backend-generated `doodle.strokes`). When
	 * provided the canvas opens pre-populated so the user can keep drawing.
	 */
	initialStrokes?: DoodleStroke[];
}

export const DoodleModal: FC<DoodleModalProps> = ({ isOpen, onClose, onSave, initialStrokes }) => {
	const { t } = useTranslation('admin_bizy');
	// The parked Bizy flight overlay sits above modals; hide it while editing.
	useSuppressBizyFlight(isOpen);
	const {
		tool,
		setTool,
		color,
		setColor,
		size,
		setSize,
		eraserSize,
		setEraserSize,
		strokes,
		currentStroke,
		startStroke,
		addPoint,
		endStroke,
		undo,
		redo,
		canUndo,
		canRedo,
		hasContent,
	} = useDoodle(initialStrokes);
	const popoverPortalRef = useRef<HTMLDivElement>(null);
	const strokesRef = useRef(strokes);
	strokesRef.current = strokes;

	const handleSave = useCallback(() => {
		// Guard on rendered content (matches the Save button's disabled state):
		// a canvas of degenerate taps/flicks has strokes but paints nothing.
		if (!hasRenderedContent(strokesRef.current)) return;
		onSave(strokesRef.current);
		onClose();
	}, [onSave, onClose]);

	const handleClose = useCallback(() => {
		onClose();
	}, [onClose]);

	useEffect(() => {
		if (!isOpen) return;

		const handleKeyDown = (e: KeyboardEvent) => {
			if (shouldIgnoreDoodleShortcut(e.target)) return;

			const isMod = e.metaKey || e.ctrlKey;

			if (isMod && e.key === 'z' && !e.shiftKey) {
				e.preventDefault();
				undo();
				return;
			}
			if (isMod && ((e.key === 'z' && e.shiftKey) || e.key === 'y')) {
				e.preventDefault();
				redo();
				return;
			}

			if (isMod || e.altKey) return;

			switch (e.key.toLowerCase()) {
				case 'b':
					setTool('brush');
					break;
				case 'e':
					setTool('eraser');
					break;
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [isOpen, undo, redo, setTool]);

	return (
		<Modal isOpen={isOpen} onClose={handleClose} size="4xl" closeOnOverlayClick={false}>
			<ModalOverlay />
			<ModalContent
				ref={popoverPortalRef}
				maxW={{ base: 'calc(100vw - 24px)', md: rem(770) }}
				mx="auto"
				p={5}
				borderRadius={{ base: 'xl', md: rem(24) }}
				bg="bg.surface"
			>
				<ModalHeader p={0} pr={10} mb={4} color="text.default" borderBottom="none">
					<Text fontSize="lg" fontWeight="semibold">
						{t('doodle.title')}
					</Text>
				</ModalHeader>
				<ModalCloseButton top={5} right={5} size="md" borderRadius="full" bg="secondary.bgAlpha" />

				<ModalBody p={0}>
					<Box borderWidth={rem(1)} borderStyle="solid" borderColor="border.default" borderRadius={rem(16)} overflow="hidden">
						<DoodleCanvas strokes={strokes} currentStroke={currentStroke} onStartStroke={startStroke} onAddPoint={addPoint} onEndStroke={endStroke} />
						<DoodleToolbar
							tool={tool}
							color={color}
							size={size}
							eraserSize={eraserSize}
							isDrawing={currentStroke !== null}
							canUndo={canUndo}
							canRedo={canRedo}
							onToolChange={setTool}
							onColorChange={setColor}
							onSizeChange={setSize}
							onEraserSizeChange={setEraserSize}
							onUndo={undo}
							onRedo={redo}
							onCancel={handleClose}
							onSave={handleSave}
							isSaveDisabled={!hasContent || currentStroke !== null}
							portalContainerRef={popoverPortalRef}
						/>
					</Box>
				</ModalBody>
			</ModalContent>
		</Modal>
	);
};
